import type { OnInit } from '@angular/core';
import { ChangeDetectionStrategy, Component, inject, input, resource } from '@angular/core';
import { RegisterTabComponent } from '@core/decorators/register-tab-component.decorator';
import { EmailListTableConfigService } from '@features/email-communication/services/email-list-table-config.service';
import { OrganizationTabKeys } from '@features/organization/constants/organization-tab-keys.constant';
import { OrganizationEmailListFacade } from '@features/organization/facades/organization-email-list.facade';
import { OrganizationEmailListFilterStore } from '@features/organization/stores/organization-email-list-filter.store';
import { TranslocoDirective } from '@jsverse/transloco';
import type { UUID } from '@kerberos-compliance/compliance-shared-ts/common/types/uuid.type';
import type { ResponseEmailDto } from '@kerberos-compliance/compliance-shared-ts/features/communication/dtos/response.email.dto';
import type { TableColumn, TableQueryParameters } from '@kerberos-compliance/compliance-shared-ui';
import { AppDatatableComponent, FILTER_FACADE_TOKEN, FilterComponent } from '@kerberos-compliance/compliance-shared-ui';
import { TitleComponent } from '@shared/components/title/title.component';
import { NzColDirective, NzRowDirective } from 'ng-zorro-antd/grid';

@RegisterTabComponent(OrganizationTabKeys.Communication)
@Component({
  selector: 'aad-organization-email-list',
  imports: [TranslocoDirective, AppDatatableComponent, FilterComponent, NzRowDirective, TitleComponent, NzColDirective],
  templateUrl: './organization-detail-tab-email-list.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    OrganizationEmailListFacade,
    EmailListTableConfigService,
    OrganizationEmailListFilterStore,
    { provide: FILTER_FACADE_TOKEN, useExisting: OrganizationEmailListFacade },
  ],
})
export class OrganizationDetailTabEmailList implements OnInit {
  public readonly organizationId = input<UUID>();

  protected readonly facade = inject(OrganizationEmailListFacade);

  // noinspection JSUnusedGlobalSymbols - placeholder to keep the resource
  protected retrieveDataOnIdChange = resource({
    params: () => this.organizationId(),
    loader: () => {
      this.facade.setOrganizationId(this.organizationId()!);
      return this.facade.loadEmails();
    },
  });

  public ngOnInit(): void {
    this.facade.initialize();
  }

  /**
   * Loads email data from the server based on table query parameters.
   *
   * @param {TableQueryParameters} parameters - Query parameters including pagination, sorting, and filters.
   * @returns {void} This method does not return a value.
   */
  public loadDataFromServer(parameters: TableQueryParameters): void {
    this.facade.loadDataFromServer(parameters);
  }

  /**
   * Persists the current notification table configuration for the organization detail view.
   *
   * @param {TableColumn<ResponseEmailDto>[]} columns - The current column configuration.
   * @returns {Promise<void>} Resolves when saving is complete.
   */
  protected async saveTableConfiguration(columns: TableColumn<ResponseEmailDto>[]): Promise<void> {
    await this.facade.saveTableConfiguration(columns);
  }
}
